/**
 * A recording's state as a room's page shows it: still being made, failed with a way to try again, or ready. A
 * recording that exists is not one that plays: a render that failed hours ago must not look finished.
 */
import type { VideoJobState, VideoStatusResponse } from "../../../shared/types";

/** The `video_jobs` states that mean the render is still working. */
const RENDERING: VideoJobState[] = ["uploading", "probing", "transcoding", "assembling", "poster", "transcribing"];

/** The card's whole story, in the words it puts on screen. */
type RecordingLook =
  | { kind: "none" }
  | { kind: "ready"; minutes: number }
  | { kind: "processing"; label: string; percent: number | null }
  | { kind: "failed"; message: string };

/** A status poll, reduced to the fields the card reads. */
export type RecordingPoll = Pick<
  VideoStatusResponse,
  "state" | "ready" | "chunks_done" | "chunk_count" | "duration_sec" | "stopped"
>;

interface RecordingSource {
  videoId: string | null;
  /** What the detail last reported, until a poll says otherwise. */
  state: VideoJobState | null;
  durationSec: number;
  poll: RecordingPoll | null;
}

/** A poll's `VideoState` in the smaller vocabulary the webinar row keeps. */
function polledState(poll: RecordingPoll): VideoJobState {
  if (poll.ready) return "ready";
  if (poll.state === "error") return "error";
  if (poll.state === "unknown") return "none";
  return (RENDERING as string[]).includes(poll.state)
    ? (poll.state as VideoJobState)
    : "transcoding";
}

/**
 * What the recording card shows. A recording that exists is not the same as a
 * recording that played: the card used to say "Looks great" the moment a video id
 * existed, including for a render that had failed hours ago.
 */
export function recordingView(src: RecordingSource): RecordingLook {
  const state = src.poll ? polledState(src.poll) : src.state;
  if (!src.videoId && !src.poll) return { kind: "none" };
  if (state === "none") return { kind: "none" };

  if (state === "error") return { kind: "failed", message: src.poll?.stopped ?? "The video didn't finish rendering." };
  if (state === "ready") {
    const seconds = src.poll && src.poll.duration_sec > 0 ? src.poll.duration_sec : src.durationSec;
    return { kind: "ready", minutes: seconds > 0 ? Math.round(seconds / 60) : 0 };
  }

  // A recording with no state at all (a row from before the column existed) is not
  // a recording we can vouch for: it waits, and the first poll settles what it is.
  //
  // The state is asked FIRST: the render's chunk counts stay at their finished
  // value through every step after it, so branching on the count first left the
  // card saying "Rendering 58 of 58" (and rewinding its bar) while the talk was
  // being put together, framed or listened to.
  if (state === "assembling") {
    return { kind: "processing", label: "Putting it together", percent: null };
  }
  if (state === "poster") {
    return { kind: "processing", label: "Taking the poster frame", percent: null };
  }
  if (state === "transcribing") {
    return { kind: "processing", label: "Listening to the talk", percent: null };
  }
  // Chunk counts only exist once the render has a plan, so they are what decides
  // between a real percentage and a bar that just says "working".
  const done = src.poll?.chunks_done ?? 0;
  const count = src.poll?.chunk_count ?? 0;
  return count > 0
    ? {
        kind: "processing",
        label: `Rendering ${done} of ${count}`,
        percent: Math.min(100, Math.round((done / count) * 100)),
      }
    : { kind: "processing", label: "Processing your recording", percent: null };
}

/** Is there anything left to wait for? */
export function shouldPollRecording(look: RecordingLook): boolean {
  return look.kind === "processing";
}

/** What a refused retry means for the card. */
type RetryOutcome =
  | { kind: "keepPolling"; toast: string }
  | { kind: "needsUpload"; message: string }
  | { kind: "showError"; message: string };

const NOT_FAILED = "That recording isn't in a failed state.";

/**
 * The retry endpoint's refusals, read as what the host should see. A job that is
 * not in `error` means our view was stale and the render is running after all, so
 * the card goes back to waiting rather than showing a failure.
 */
export function retryOutcome(status: number, message: string): RetryOutcome {
  if (status === 409 && message === NOT_FAILED) {
    return { kind: "keepPolling", toast: "That render is already running." };
  }
  if (status === 409 || status === 404) {
    return { kind: "needsUpload", message: message || "Upload the recording again." };
  }
  return { kind: "showError", message: message || "We couldn't restart that render." };
}

/** A stand-in poll for a render we know has just (re)started. */
export function restartedPoll(): RecordingPoll {
  return { state: "probing", ready: false, chunks_done: 0, chunk_count: 0, duration_sec: 0 };
}
